/**
 * Attribution Capture - Collects all tracking identifiers
 *
 * Captures GA4, Meta, UTM, and click ID parameters for proper attribution.
 * This data is attached to every tracked event.
 */

import type { AttributionData } from "./types";

// Constants
const GOOGLE_ADS_PARAMS = ["gclid", "gbraid", "wbraid"] as const;
const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;
const CLICK_ID_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
] as const;

// ============================================
// COOKIE HELPERS
// ============================================

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// ============================================
// GA4 ATTRIBUTION
// ============================================

/**
 * Get the raw GA4 session cookie value
 * Cookie format: _ga_<MEASUREMENT_ID>=GS1.1.{session_id}.{session_count}...
 */
function getGA4SessionCookieValue(): string | null {
  if (typeof document === "undefined") return null;

  const measurementId =
    (typeof window !== "undefined" &&
      (window as { GA_TRACKING_ID?: string }).GA_TRACKING_ID) ||
    "G-C6FWHK8HR3";

  // Try both cookie name formats
  const cookieNameWithDashes = `_ga_${measurementId}`;
  const cookieNameWithUnderscores = `_ga_${measurementId.replace(/-/g, "_")}`;

  let cookieValue = getCookie(cookieNameWithDashes);
  if (!cookieValue) {
    cookieValue = getCookie(cookieNameWithUnderscores);
  }

  return cookieValue;
}

/**
 * Get GA4 client_id via gtag or cookie fallback
 */
async function getGA4ClientId(): Promise<string | null> {
  if (typeof window === "undefined") return null;

  return new Promise(resolve => {
    if (typeof window.gtag === "function") {
      const MEASUREMENT_ID =
        (window as { GA_TRACKING_ID?: string }).GA_TRACKING_ID || "";
      if (MEASUREMENT_ID) {
        let resolved = false;
        const fallback = () => {
          if (resolved) return;
          resolved = true;
          resolve(parseGaCookie());
        };

        const timeoutId = window.setTimeout(fallback, 500);
        (window as { gtag: (...args: unknown[]) => void }).gtag(
          "get",
          MEASUREMENT_ID,
          "client_id",
          (cid: string) => {
            if (resolved) return;
            resolved = true;
            window.clearTimeout(timeoutId);
            if (cid) return resolve(cid);
            resolve(parseGaCookie());
          }
        );
        return;
      }
    }
    resolve(parseGaCookie());
  });
}

function parseGaCookie(): string | null {
  const m =
    typeof document !== "undefined"
      ? document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/)
      : null;
  return m ? m[1] : null;
}

/**
 * Get GA4 session_id from cookie (returns NUMBER for Measurement Protocol)
 */
function getGA4SessionId(): number | null {
  const cookieValue = getGA4SessionCookieValue();
  if (!cookieValue) return null;

  try {
    const parts = cookieValue.split(".");
    if (parts.length >= 3 && parts[0] === "GS1" && parts[1] === "1") {
      const sessionId = parseInt(parts[2], 10);
      return isNaN(sessionId) ? null : sessionId;
    }
  } catch {
    // If parsing fails, return null
  }

  return null;
}

/**
 * Get GA4 session_number from cookie
 * CRITICAL: GA4 Measurement Protocol requires BOTH session_id AND session_number
 */
function getGA4SessionNumber(): number | null {
  const cookieValue = getGA4SessionCookieValue();
  if (!cookieValue) return null;

  try {
    const parts = cookieValue.split(".");
    if (parts.length >= 4 && parts[0] === "GS1" && parts[1] === "1") {
      const sessionNumber = parseInt(parts[3], 10);
      return isNaN(sessionNumber) ? null : sessionNumber;
    }
  } catch {
    // If parsing fails, return null
  }

  return null;
}

// ============================================
// UTM & CLICK ID PERSISTENCE
// ============================================

function getUrlParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  return url.searchParams.get(name);
}

function persistParam(key: string, value: string): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        `tracking:${key}`,
        JSON.stringify({ value, timestamp: Date.now() })
      );
    }
  } catch {
    // Ignore storage errors
  }
}

function getPersistedParam(key: string, maxAge = 30 * 24 * 60 * 60 * 1000): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(`tracking:${key}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.timestamp && Date.now() - parsed.timestamp < maxAge) {
        return parsed.value;
      }
      window.localStorage.removeItem(`tracking:${key}`);
    }
  } catch {
    // Ignore storage errors
  }
  return null;
}

function getAndPersistParam(name: string): string | null {
  const urlValue = getUrlParam(name);
  if (urlValue) {
    persistParam(name, urlValue);
    return urlValue;
  }
  return getPersistedParam(name);
}

// ============================================
// MAIN CAPTURE FUNCTION
// ============================================

export const AttributionCapture = {
  /**
   * Capture all attribution data for an event
   */
  async capture(): Promise<AttributionData> {
    if (typeof window === "undefined") {
      return this.getEmptyAttribution();
    }

    // Capture and persist click IDs from URL
    CLICK_ID_PARAMS.forEach(param => getAndPersistParam(param));
    UTM_PARAMS.forEach(param => getAndPersistParam(param));

    return {
      // GA4
      ga4ClientId: await getGA4ClientId(),
      ga4SessionId: getGA4SessionId(),
      ga4SessionNumber: getGA4SessionNumber(),

      // Meta
      fbp: getCookie("_fbp"),
      fbc: (() => {
        const cookie = getCookie("_fbc");
        if (cookie) return cookie;
        // Fallback: construct _fbc from fbclid when pixel hasn't set the cookie yet
        const fbclid = getAndPersistParam("fbclid");
        if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
        return null;
      })(),

      // UTM (from URL or localStorage)
      utmSource: getAndPersistParam("utm_source"),
      utmMedium: getAndPersistParam("utm_medium"),
      utmCampaign: getAndPersistParam("utm_campaign"),
      utmTerm: getAndPersistParam("utm_term"),
      utmContent: getAndPersistParam("utm_content"),
      utmId: getAndPersistParam("utm_id"),

      // Click IDs
      gclid: getAndPersistParam("gclid"),
      gbraid: getAndPersistParam("gbraid"),
      wbraid: getAndPersistParam("wbraid"),
      fbclid: getAndPersistParam("fbclid"),
      msclkid: getAndPersistParam("msclkid"),
      ttclid: getAndPersistParam("ttclid"),

      // Page context
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer || null,
      userAgent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },

  /**
   * Get empty attribution (for SSR)
   */
  getEmptyAttribution(): AttributionData {
    return {
      ga4ClientId: null,
      ga4SessionId: null,
      ga4SessionNumber: null,
      fbp: null,
      fbc: null,
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      utmTerm: null,
      utmContent: null,
      utmId: null,
      gclid: null,
      gbraid: null,
      wbraid: null,
      fbclid: null,
      msclkid: null,
      ttclid: null,
      pageUrl: "",
      pagePath: "",
      pageTitle: "",
      referrer: null,
      userAgent: "",
      language: "",
      timezone: "",
    };
  },

  /**
   * Get Google Ads IDs (for backwards compatibility)
   */
  getGoogleAdsIds(): { gclid: string | null; gbraid: string | null; wbraid: string | null } {
    return {
      gclid: getAndPersistParam("gclid"),
      gbraid: getAndPersistParam("gbraid"),
      wbraid: getAndPersistParam("wbraid"),
    };
  },

  /**
   * Get UTM params (for backwards compatibility)
   */
  getUtmParams(): Record<string, string | null> {
    return {
      utm_source: getAndPersistParam("utm_source"),
      utm_medium: getAndPersistParam("utm_medium"),
      utm_campaign: getAndPersistParam("utm_campaign"),
      utm_term: getAndPersistParam("utm_term"),
      utm_content: getAndPersistParam("utm_content"),
      utm_id: getAndPersistParam("utm_id"),
    };
  },
};

// Export individual functions for backwards compatibility
export { getGA4ClientId, getGA4SessionId, getGA4SessionNumber };
